// [VV9-TEARDOWN] all-paypal-express A/B test: visibility rule (single source
// of truth).
//
// Client-safe: this module must NOT import `next/server` (unlike
// `../middleware/paypal.ts`), so it can be pulled into client bundles: both
// the express-checkout button gate (`@local/payment`) and the
// accepted-payments logo wrapper (`@local/ui`) import from here (via the
// shared `usePayPalVisibility` hook) to stay locked in step.
//
// ─── HOW THIS TEST IS ASSIGNED ──────────────────────────────────────────────
// Standard house pattern: deterministic FNV-1a bucketing of IP + User-Agent
// into a 50 / 50 control / test split: see `../middleware/paypal.ts`.
//
// Reused from the original, US-only `vv9` test (graduated in #4026: PayPal
// Express made permanently on everywhere, `market !== "us"` deleted as the
// gate). This run is GLOBAL: every market is in scope, not only the US. The
// `market` parameter that used to carry the US-only gate is gone entirely; it
// is no longer part of the rule.
//
// Unlike every other live `vvN` test (`vv5`/`vv11`/`vv13`, all scoped to one
// LP tree), PayPal Express and the accepted-payments logo render on many
// unrelated surfaces: home, PDP, LP, cart, checkout, footer. There is no
// narrower path scope to check, so `paypalMiddleware` buckets every request
// that reaches it (i.e. everything `isValidPath` already lets through
// `AppMiddleware`). This is still a Route-level test in the doc's taxonomy
// (cookie assigned in middleware); it is scope-unrestricted rather than
// path-restricted. See `docs/experimentation-ab-test-all-paypal-express.md`.
//
// Labels preserve the ORIGINAL pre-graduation mapping: `control` = PayPal
// hidden, `test` = PayPal shown. Only an explicit `control` cookie hides
// PayPal: no cookie means NOT ENROLLED, not `control`, so the fail-safe
// default stays today's shipped "always show" behavior. That distinction is
// load-bearing: apps that don't opt in (apps/mirror-anchor) never get a vv9
// cookie, yet they render the shared `Footer` (→ `CreditCardsPayPal`) and
// `wrappers/anchor.tsx` → `ExpressCheckout` → `PayPalExpress`. Defaulting a
// missing cookie to hidden would have removed PayPal for 100% of mirror
// traffic (PR #4149 review). Same reasoning covers an `AppMiddleware` catch:
// it returns a bare `NextResponse.next()` with no cookie, and those visitors
// should see production behavior rather than a silent control experience.
//
// Resuming this test still removes PayPal Express for ~50% of traffic on
// opted-in apps in every market; do not go live without PostHog metrics
// configured and Revenue/CRO sign-off (doc §1.1a).
//
// ─── TEARDOWN CHECKLIST (grep `VV9-TEARDOWN` across the repo) ───────────────
// DELETE these files (entirely test-only):
//   packages/tracking/src/experiments/paypalVisibility.ts        (this file)
//   packages/tracking/src/experiments/usePayPalVisibility.ts
//   packages/tracking/src/middleware/paypal.ts
//   packages/tracking/src/__tests__/paypalMiddleware.test.ts
//   packages/tracking/src/__tests__/paypalExperiment.test.ts
//   packages/ui/src/components/ui/CreditCards/__tests__/CreditCardsPayPal.test.tsx
//
// REVERT the marked `[VV9-TEARDOWN]` blocks in:
//   packages/tracking/src/middleware/index.ts        (option, resolver, header, cookie)
//   packages/tracking/src/middleware/url/const.ts     (drop the "vv9" entry outright, the #4026 convention)
//   packages/tracking/src/trackActions.tsx            (split_test membership)
//   apps/store/src/proxy.ts                           (drop `vv9: true`)
//   packages/ui/src/components/ui/CreditCards/CreditCardsPayPal.tsx (delete; restore hardcoded `showPayPal` at every call site below)
//   packages/ui/src/components/Blocks/PromoBannerWithStars/index.tsx
//   packages/ui/src/components/Blocks/Highlight/index.tsx
//   packages/ui/src/components/Blocks/PromoHeadlineVideoWithStars/index.tsx
//   packages/offer/src/modules/store/ProductPage/index.tsx
//   packages/offer/src/modules/store/Cart/CartContents.tsx
//   packages/offer/src/modules/common/Footer/index.tsx
//   packages/payment/src/modules/paypal/express.tsx  (drop the visibility gate)
//   packages/offer/src/modules/common/StaticPages/CookiePolicy/en.tsx (vv9 cookie row)
//   packages/offer/src/modules/common/StaticPages/CookiePolicy/de.tsx (vv9 cookie row, DE)
//   apps/store/src/tests/policy-pages.spec.ts        (vv9 cookie-policy assertion)
//   packages/devtools/src/panel/components/ABTestingTab.tsx (vv9 EXPERIMENTS entry)
//   packages/tracking/src/__tests__/appMiddleware.test.ts (revert to a "vv9 all-paypal-express teardown" describe block, adapted to whichever arm won)
//
// GRADUATION NOTE: if the test concludes and PayPal Express should be
// permanently shown (or hidden) again, make the outcome a real, hardcoded
// decision (delete the gate and hardcode the winning behaviour at each call
// site above) rather than leaving this cookie gate in place. Consider
// whether the outcome should differ per market this time, since #4026's
// "always show everywhere" graduation is exactly what is being re-tested.
//
// EXTERNAL: recreate the `all-paypal-express` PostHog feature flag (key,
// variants `control`/`test`), get Data to configure metrics, and get
// Revenue/CRO sign-off before go-live (doc §1.1, §1.1a).
// ───────────────────────────────────────────────────────────────────────────

/** Cookie / query-override slot name for the vv9 experiment. */
export const VV9_COOKIE = "vv9"

/**
 * PostHog feature-flag key for this experiment (single source of truth).
 * The `control` / `test` cookie buckets map 1:1 to this flag's variant keys.
 * Reused from the original US-only run: same key, same slot, now global.
 */
export const VV9_FEATURE_FLAG = "all-paypal-express"

/**
 * Valid variant values for the vv9 experiment.
 *   - control → PayPal Express button + accepted-payments logo hidden (the
 *               original pre-graduation US "control" experience)
 *   - test    → PayPal Express button + logo shown
 */
export const VV9_VARIANTS = ["control", "test"] as const

export type Vv9Variant = (typeof VV9_VARIANTS)[number]

/** Type guard for anything read back from the cookie / query override. */
export const isVv9Variant = (value: unknown): value is Vv9Variant =>
  VV9_VARIANTS.includes(value as Vv9Variant)

/**
 * Whether PayPal should be shown (button and logo) for a given vv9 variant.
 * `control` hides it; everything else (`test`, a missing cookie, a garbage
 * cookie) shows it, because absence of the cookie means the visitor was
 * never bucketed (unopted app, or a middleware catch) and un-enrolled traffic
 * must keep today's shipped behavior. See the header note.
 *
 * No `market` parameter: the whole point of resuming this test globally is
 * that market is no longer part of the rule.
 *
 * @param vv9 - Raw cookie / query-override value, unvalidated.
 * @returns `true` when the PayPal button + logo should render.
 */
export const shouldShowPayPal = (vv9?: string | null): boolean =>
  vv9 !== "control"
